import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { assetsService, type AssetDetail } from '@/services/assets.service';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AuthImage } from '@/components/ui/auth-image';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface Props {
  assetId: string | null;
  onClose: () => void;
  fetchFn?: (id: string) => Promise<AssetDetail>;
  /** Where photo/invoice files are served from. Self-service callers must use the
   *  `/assets/my/documents` route — the admin route needs `assets.view` + the assets_list module. */
  documentBasePath?: string;
  /** My Assets shows photos only. */
  hideInvoices?: boolean;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function durationLabel(from: string, to: string | null): string {
  const start = new Date(from);
  const end = to ? new Date(to) : new Date();
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000);
  if (days < 1) return 'Less than a day';
  if (days === 1) return '1 day';
  if (days < 30) return `${days} days`;
  const months = Math.floor(days / 30);
  return `${months} month${months !== 1 ? 's' : ''}`;
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm">{value ?? '—'}</dd>
    </div>
  );
}

export function AssetDetailDrawer({ assetId, onClose, fetchFn, documentBasePath = '/assets/documents', hideInvoices = false }: Props) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const resolver = fetchFn ?? assetsService.getAsset;
  const { data: asset, isLoading, isError } = useQuery({
    queryKey: ['assets', fetchFn ? 'my-detail' : 'detail', assetId],
    queryFn: () => resolver(assetId!),
    enabled: !!assetId,
  });

  const photos = asset?.documents.filter((d) => d.document_type === 'photo') ?? [];
  const invoices = hideInvoices ? [] : asset?.documents.filter((d) => d.document_type === 'invoice') ?? [];

  return (
    <Dialog open={!!assetId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {isLoading && (
          <>
            <DialogTitle className="sr-only">Loading asset details</DialogTitle>
            <div className="space-y-4 p-2">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-4 animate-pulse rounded bg-muted" />
              ))}
            </div>
          </>
        )}

        {isError && !isLoading && (
          <>
            <DialogTitle className="sr-only">Error</DialogTitle>
            <div className="py-10 text-center text-sm text-destructive">
              Failed to load asset details.
            </div>
          </>
        )}

        {asset && !isLoading && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 flex-wrap">
                <code className="font-mono text-xl">{asset.asset_code}</code>
                {asset.status && (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold text-white"
                    style={{ backgroundColor: asset.status.color }}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-white/70" />
                    {asset.status.name}
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>

            {/* Info grid */}
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-t border-border/60 pt-4">
              <InfoRow label="Category" value={asset.category?.name} />
              <InfoRow label="Sub Category" value={asset.subcategory?.name} />
              <InfoRow label="Brand" value={asset.brand?.name} />
              <InfoRow label="Model" value={asset.model_name} />
              <InfoRow label="Purchase Date" value={formatDate(asset.purchase_date)} />
              <InfoRow
                label="Warranty"
                value={
                  asset.warranty_available && asset.warranty_end_date
                    ? `Until ${formatDate(asset.warranty_end_date)}`
                    : asset.warranty_available
                    ? 'Yes'
                    : 'N/A'
                }
              />
            </dl>

            {/* Assigned To / Department */}
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-t border-border/60 pt-3">
              <InfoRow
                label="Assigned To"
                value={asset.assigned_to?.name ?? <span className="text-muted-foreground">Unassigned</span>}
              />
              <InfoRow
                label="Department"
                value={asset.department?.name ?? <span className="text-muted-foreground">—</span>}
              />
            </dl>

            {/* Notes */}
            {asset.notes && (
              <div className="border-t border-border/60 pt-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Notes</p>
                <p className="text-sm whitespace-pre-wrap">{asset.notes}</p>
              </div>
            )}

            {/* Photos */}
            {photos.length > 0 && (
              <div className="border-t border-border/60 pt-3 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">
                  Photos ({photos.length})
                </p>
                <div className={`grid gap-2 ${photos.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  {photos.map((doc, i) => (
                    <button
                      key={doc.id}
                      type="button"
                      onClick={() => setLightboxIndex(i)}
                      className="group relative overflow-hidden rounded-lg border border-border/60 focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <AuthImage
                        src={`${documentBasePath}/${doc.id}/file`}
                        alt={doc.original_name}
                        className={`w-full object-cover transition-transform duration-200 group-hover:scale-105 ${
                          photos.length === 1 ? 'max-h-72' : 'h-40'
                        }`}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
                        <span className="opacity-0 group-hover:opacity-100 text-white text-xs font-medium bg-black/50 px-2 py-1 rounded transition-opacity">
                          View full
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Lightbox */}
            {lightboxIndex !== null && photos.length > 0 && (
              <div
                className="fixed inset-0 z-modal flex items-center justify-center bg-black/90"
                onClick={() => setLightboxIndex(null)}
              >
                {/* Close */}
                <button
                  type="button"
                  className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/40 rounded-full p-1.5"
                  onClick={() => setLightboxIndex(null)}
                >
                  <X className="h-5 w-5" />
                </button>

                {/* Prev */}
                {photos.length > 1 && (
                  <button
                    type="button"
                    className="absolute left-4 text-white/80 hover:text-white bg-black/40 rounded-full p-2"
                    onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex - 1 + photos.length) % photos.length); }}
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                )}

                {/* Image */}
                <div onClick={(e) => e.stopPropagation()} className="max-w-[90vw] max-h-[90vh]">
                  <AuthImage
                    src={`${documentBasePath}/${photos[lightboxIndex]!.id}/file`}
                    alt={photos[lightboxIndex]!.original_name}
                    className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
                  />
                  {photos.length > 1 && (
                    <p className="text-center text-white/60 text-xs mt-2">
                      {lightboxIndex + 1} / {photos.length}
                    </p>
                  )}
                </div>

                {/* Next */}
                {photos.length > 1 && (
                  <button
                    type="button"
                    className="absolute right-4 text-white/80 hover:text-white bg-black/40 rounded-full p-2"
                    onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex + 1) % photos.length); }}
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                )}
              </div>
            )}

            {/* Invoice documents */}
            {invoices.length > 0 && (
              <div className="border-t border-border/60 pt-3 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Invoice / Bill Documents</p>
                <ul className="space-y-1.5">
                  {invoices.map((doc) => (
                    <li
                      key={doc.id}
                      className="flex items-center justify-between rounded-md border border-border/60 bg-muted/30 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{doc.original_name}</p>
                        <p className="text-xs text-muted-foreground">{formatBytes(doc.size_bytes)}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="ml-3 shrink-0 h-7 gap-1 px-2 text-xs"
                        onClick={() => assetsService.downloadDocument(doc.id, doc.original_name, documentBasePath)}
                      >
                        <Download className="h-3 w-3" />
                        Download
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Assignment History */}
            {asset.assignment_history.length > 0 && (
              <div className="border-t border-border/60 pt-3 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Assignment History</p>
                <div className="overflow-x-auto rounded-md border border-border/60">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Assigned To</th>
                        <th className="px-3 py-2 text-left font-semibold">By</th>
                        <th className="px-3 py-2 text-left font-semibold">Date</th>
                        <th className="px-3 py-2 text-left font-semibold">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                      {asset.assignment_history.map((h) => (
                        <tr key={h.id} className="hover:bg-muted/20">
                          <td className="px-3 py-2">
                            {h.assigned_to?.name ?? (
                              <span className="text-muted-foreground">—</span>
                            )}
                            {h.department && (
                              <span className="block text-muted-foreground">
                                {h.department.name}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {h.assigned_by?.name ?? '—'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {formatDate(h.assigned_at)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {durationLabel(h.assigned_at, h.unassigned_at)}
                            {!h.unassigned_at && (
                              <Badge variant="secondary" className="ml-1.5 text-[10px]">
                                Current
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
